import { queryOptions, useQuery } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import {
  AffiliationSchema,
  AffiliatePixelSchema,
  MarketplaceProductSchema,
} from './affiliation.contract'
import type { Affiliation, AffiliatePixel, MarketplaceProduct } from './affiliation.contract'
import { AffiliateCommissionChangeSchema } from './commission-history.contract'
import type { AffiliateCommissionChange } from './commission-history.contract'
import { AffiliateCommissionScopeSchema } from './commission-scope.contract'
import type { AffiliateCommissionScope } from './commission-scope.contract'

export const affiliationKeys = {
  marketplace: () => ['partnerships', 'marketplace'] as const,
  mine: () => ['partnerships', 'affiliations', 'mine'] as const,
  forProduct: (productId: string) =>
    ['partnerships', 'affiliations', 'product', productId] as const,
  pixels: (affiliationId: string) =>
    ['partnerships', 'affiliations', affiliationId, 'pixels'] as const,
  commissionHistory: (productId: string) =>
    ['partnerships', 'commission-history', productId] as const,
}

export async function getMarketplaceProducts(): Promise<MarketplaceProduct[]> {
  const data = await apiFetch<unknown>('/partnerships/marketplace')
  return MarketplaceProductSchema.array().parse(data)
}

export async function getMyAffiliations(): Promise<Affiliation[]> {
  const data = await apiFetch<unknown>('/partnerships/affiliations/mine')
  return AffiliationSchema.array().parse(data)
}

export async function getAffiliationsForProduct(productId: string): Promise<Affiliation[]> {
  const data = await apiFetch<unknown>(`/partnerships/products/${productId}/affiliations`)
  return AffiliationSchema.array().parse(data)
}

export async function getCommissionHistory(
  productId: string,
): Promise<AffiliateCommissionChange[]> {
  const data = await apiFetch<unknown>(`/partnerships/products/${productId}/commission-history`)
  return AffiliateCommissionChangeSchema.array().parse(data)
}

export async function getAffiliatePixels(affiliationId: string): Promise<AffiliatePixel[]> {
  const data = await apiFetch<unknown>(`/partnerships/affiliations/${affiliationId}/pixels`)
  return AffiliatePixelSchema.array().parse(data)
}

export function marketplaceProductsQuery() {
  return queryOptions({
    queryKey: affiliationKeys.marketplace(),
    queryFn: getMarketplaceProducts,
  })
}

export function myAffiliationsQuery() {
  return queryOptions({
    queryKey: affiliationKeys.mine(),
    queryFn: getMyAffiliations,
  })
}

export function affiliationsForProductQuery(productId: string) {
  return queryOptions({
    queryKey: affiliationKeys.forProduct(productId),
    queryFn: () => getAffiliationsForProduct(productId),
    enabled: Boolean(productId),
  })
}

export function commissionHistoryQuery(productId: string) {
  return queryOptions({
    queryKey: affiliationKeys.commissionHistory(productId),
    queryFn: () => getCommissionHistory(productId),
    enabled: Boolean(productId),
  })
}

export function affiliatePixelsQuery(affiliationId: string) {
  return queryOptions({
    queryKey: affiliationKeys.pixels(affiliationId),
    queryFn: () => getAffiliatePixels(affiliationId),
    enabled: Boolean(affiliationId),
  })
}

export async function getAffiliateCommissionScope(
  productId: string,
): Promise<AffiliateCommissionScope> {
  const data = await apiFetch<unknown>(`/catalog/products/${productId}/affiliate-scope`)
  return AffiliateCommissionScopeSchema.parse(data)
}

export function affiliateCommissionScopeQuery(productId: string) {
  return queryOptions({
    queryKey: ['partnerships', 'commission-scope', productId] as const,
    queryFn: () => getAffiliateCommissionScope(productId),
    enabled: Boolean(productId),
  })
}

export function useAffiliateCommissionScopeQuery(productId: string | undefined) {
  return useQuery(affiliateCommissionScopeQuery(productId ?? ''))
}

export function useMarketplaceProductsQuery() {
  return useQuery(marketplaceProductsQuery())
}

export function useMyAffiliationsQuery() {
  return useQuery(myAffiliationsQuery())
}

export function useAffiliationsForProductQuery(productId: string | undefined) {
  return useQuery(affiliationsForProductQuery(productId ?? ''))
}

export function useAffiliatePixelsQuery(affiliationId: string | undefined) {
  return useQuery(affiliatePixelsQuery(affiliationId ?? ''))
}

export function useCommissionHistoryQuery(productId: string | undefined) {
  return useQuery(commissionHistoryQuery(productId ?? ''))
}
